/**
 * Wealth Snapshot Manager
 * CRUD operations for managing wealth snapshots in Firestore
 */

import {
  collection,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  getDocs,
  getDoc,
  query,
  where,
  orderBy,
  limit,
  Timestamp
} from 'firebase/firestore';
import { db } from '../../firebase';

/**
 * Create a new wealth snapshot
 * @param {string} userId - User ID
 * @param {Object} snapshotData - Snapshot data to save
 * @returns {Promise<Object>} Created snapshot with ID
 */
export const createSnapshot = async (userId, snapshotData) => {
  if (!userId) {
    throw new Error('User ID is required');
  }

  try {
    // Convert Date to Firestore Timestamp
    const dataToSave = {
      ...snapshotData,
      timestamp: Timestamp.fromDate(snapshotData.timestamp || new Date()),
      createdAt: Timestamp.fromDate(snapshotData.createdAt || new Date())
    };

    const snapshotsRef = collection(db, 'users', userId, 'wealthSnapshots');
    const docRef = await addDoc(snapshotsRef, dataToSave);

    return {
      id: docRef.id,
      ...dataToSave
    };
  } catch (error) {
    console.error('Error creating snapshot:', error);
    throw new Error('Failed to create wealth snapshot');
  }
};

/**
 * Update an existing wealth snapshot
 * @param {string} userId - User ID
 * @param {string} snapshotId - Snapshot ID
 * @param {Object} updates - Fields to update
 * @returns {Promise<void>}
 */
export const updateSnapshot = async (userId, snapshotId, updates) => {
  if (!userId || !snapshotId) {
    throw new Error('User ID and Snapshot ID are required');
  }

  try {
    const snapshotRef = doc(db, 'users', userId, 'wealthSnapshots', snapshotId);

    // Convert Date objects to Firestore Timestamps
    const updatesToSave = { ...updates };
    if (updatesToSave.timestamp instanceof Date) {
      updatesToSave.timestamp = Timestamp.fromDate(updatesToSave.timestamp);
    }
    if (updatesToSave.updatedAt instanceof Date) {
      updatesToSave.updatedAt = Timestamp.fromDate(updatesToSave.updatedAt);
    }

    await updateDoc(snapshotRef, updatesToSave);
  } catch (error) {
    console.error('Error updating snapshot:', error);
    throw new Error('Failed to update wealth snapshot');
  }
};

/**
 * Delete a wealth snapshot
 * @param {string} userId - User ID
 * @param {string} snapshotId - Snapshot ID
 * @returns {Promise<void>}
 */
export const deleteSnapshot = async (userId, snapshotId) => {
  if (!userId || !snapshotId) {
    throw new Error('User ID and Snapshot ID are required');
  }

  try {
    const snapshotRef = doc(db, 'users', userId, 'wealthSnapshots', snapshotId);
    await deleteDoc(snapshotRef);
  } catch (error) {
    console.error('Error deleting snapshot:', error);
    throw new Error('Failed to delete wealth snapshot');
  }
};

/**
 * Get a single snapshot by ID
 * @param {string} userId - User ID
 * @param {string} snapshotId - Snapshot ID
 * @returns {Promise<Object|null>} Snapshot data or null
 */
export const getSnapshot = async (userId, snapshotId) => {
  if (!userId || !snapshotId) {
    throw new Error('User ID and Snapshot ID are required');
  }

  try {
    const snapshotRef = doc(db, 'users', userId, 'wealthSnapshots', snapshotId);
    const snapshotDoc = await getDoc(snapshotRef);

    if (!snapshotDoc.exists()) {
      return null;
    }

    return {
      id: snapshotDoc.id,
      ...snapshotDoc.data()
    };
  } catch (error) {
    console.error('Error getting snapshot:', error);
    throw new Error('Failed to retrieve wealth snapshot');
  }
};

/**
 * Get all snapshots for a user
 * @param {string} userId - User ID
 * @param {Object} options - Query options
 * @returns {Promise<Array>} Array of snapshots
 */
export const getAllSnapshots = async (userId, options = {}) => {
  if (!userId) {
    throw new Error('User ID is required');
  }

  try {
    const snapshotsRef = collection(db, 'users', userId, 'wealthSnapshots');
    let q = query(snapshotsRef, orderBy('month', 'desc'));

    // Apply limit if specified
    if (options.limit) {
      q = query(q, limit(options.limit));
    }

    const querySnapshot = await getDocs(q);
    const snapshots = [];

    querySnapshot.forEach((doc) => {
      snapshots.push({
        id: doc.id,
        ...doc.data()
      });
    });

    return snapshots;
  } catch (error) {
    console.error('Error getting snapshots:', error);
    throw new Error('Failed to retrieve wealth snapshots');
  }
};

/**
 * Get snapshots within a date range
 * @param {string} userId - User ID
 * @param {string} startMonth - Start month (YYYY-MM)
 * @param {string} endMonth - End month (YYYY-MM)
 * @returns {Promise<Array>} Array of snapshots
 */
export const getSnapshotsByDateRange = async (userId, startMonth, endMonth) => {
  if (!userId) {
    throw new Error('User ID is required');
  }

  try {
    const snapshotsRef = collection(db, 'users', userId, 'wealthSnapshots');
    const q = query(
      snapshotsRef,
      where('month', '>=', startMonth),
      where('month', '<=', endMonth),
      orderBy('month', 'asc')
    );

    const querySnapshot = await getDocs(q);
    const snapshots = [];

    querySnapshot.forEach((doc) => {
      snapshots.push({
        id: doc.id,
        ...doc.data()
      });
    });

    return snapshots;
  } catch (error) {
    console.error('Error getting snapshots by date range:', error);
    throw new Error('Failed to retrieve wealth snapshots');
  }
};

/**
 * Get the most recent snapshot
 * @param {string} userId - User ID
 * @returns {Promise<Object|null>} Most recent snapshot or null
 */
export const getMostRecentSnapshot = async (userId) => {
  if (!userId) {
    throw new Error('User ID is required');
  }

  try {
    const snapshotsRef = collection(db, 'users', userId, 'wealthSnapshots');
    const q = query(snapshotsRef, orderBy('month', 'desc'), limit(1));

    const querySnapshot = await getDocs(q);

    if (querySnapshot.empty) {
      return null;
    }

    const doc = querySnapshot.docs[0];
    return {
      id: doc.id,
      ...doc.data()
    };
  } catch (error) {
    console.error('Error getting most recent snapshot:', error);
    throw new Error('Failed to retrieve most recent wealth snapshot');
  }
};

/**
 * Get snapshot for a specific month
 * @param {string} userId - User ID
 * @param {string} month - Month in YYYY-MM format
 * @returns {Promise<Object|null>} Snapshot for the month or null
 */
export const getSnapshotByMonth = async (userId, month) => {
  if (!userId || !month) {
    throw new Error('User ID and month are required');
  }

  try {
    const snapshotsRef = collection(db, 'users', userId, 'wealthSnapshots');
    const q = query(snapshotsRef, where('month', '==', month), limit(1));

    const querySnapshot = await getDocs(q);

    if (querySnapshot.empty) {
      return null;
    }

    const doc = querySnapshot.docs[0];
    return {
      id: doc.id,
      ...doc.data()
    };
  } catch (error) {
    console.error('Error getting snapshot by month:', error);
    throw new Error('Failed to retrieve wealth snapshot for specified month');
  }
};

/**
 * Get the previous snapshot relative to a given month
 * @param {string} userId - User ID
 * @param {string} currentMonth - Current month (YYYY-MM)
 * @returns {Promise<Object|null>} Previous snapshot or null
 */
export const getPreviousSnapshot = async (userId, currentMonth) => {
  if (!userId || !currentMonth) {
    throw new Error('User ID and current month are required');
  }

  try {
    const snapshotsRef = collection(db, 'users', userId, 'wealthSnapshots');
    const q = query(
      snapshotsRef,
      where('month', '<', currentMonth),
      orderBy('month', 'desc'),
      limit(1)
    );

    const querySnapshot = await getDocs(q);

    if (querySnapshot.empty) {
      return null;
    }

    const doc = querySnapshot.docs[0];
    return {
      id: doc.id,
      ...doc.data()
    };
  } catch (error) {
    console.error('Error getting previous snapshot:', error);
    throw new Error('Failed to retrieve previous wealth snapshot');
  }
};

/**
 * Check if a snapshot exists for a given month
 * @param {string} userId - User ID
 * @param {string} month - Month in YYYY-MM format
 * @returns {Promise<boolean>} True if exists, false otherwise
 */
export const snapshotExistsForMonth = async (userId, month) => {
  if (!userId || !month) {
    return false;
  }

  try {
    const snapshot = await getSnapshotByMonth(userId, month);
    return snapshot !== null;
  } catch (error) {
    console.error('Error checking snapshot existence:', error);
    return false;
  }
};

/**
 * Get snapshot count for a user
 * @param {string} userId - User ID
 * @returns {Promise<number>} Number of snapshots
 */
export const getSnapshotCount = async (userId) => {
  if (!userId) {
    throw new Error('User ID is required');
  }

  try {
    const snapshots = await getAllSnapshots(userId);
    return snapshots.length;
  } catch (error) {
    console.error('Error getting snapshot count:', error);
    return 0;
  }
};

/**
 * Get months with missing snapshots (gaps in the data)
 * @param {string} userId - User ID
 * @returns {Promise<Array>} Array of missing month strings (YYYY-MM)
 */
export const getMissingMonths = async (userId) => {
  if (!userId) {
    throw new Error('User ID is required');
  }

  try {
    const snapshots = await getAllSnapshots(userId);

    if (snapshots.length < 2) {
      return [];
    }

    // Sort snapshots by month ascending
    const sortedSnapshots = snapshots.sort((a, b) => a.month.localeCompare(b.month));

    const firstMonth = sortedSnapshots[0].month;
    const lastMonth = sortedSnapshots[sortedSnapshots.length - 1].month;

    // Generate all months between first and last
    const allMonths = [];
    let currentDate = new Date(firstMonth + '-01');
    const endDate = new Date(lastMonth + '-01');

    while (currentDate <= endDate) {
      const monthStr = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}`;
      allMonths.push(monthStr);
      currentDate.setMonth(currentDate.getMonth() + 1);
    }

    // Find missing months
    const existingMonths = new Set(snapshots.map(s => s.month));
    const missingMonths = allMonths.filter(month => !existingMonths.has(month));

    return missingMonths;
  } catch (error) {
    console.error('Error getting missing months:', error);
    return [];
  }
};

/**
 * Bulk delete snapshots
 * @param {string} userId - User ID
 * @param {Array} snapshotIds - Array of snapshot IDs to delete
 * @returns {Promise<Object>} Result summary
 */
export const bulkDeleteSnapshots = async (userId, snapshotIds) => {
  if (!userId || !Array.isArray(snapshotIds)) {
    throw new Error('User ID and snapshot IDs array are required');
  }

  const results = {
    deleted: 0,
    failed: 0,
    errors: []
  };

  for (const snapshotId of snapshotIds) {
    try {
      await deleteSnapshot(userId, snapshotId);
      results.deleted++;
    } catch (error) {
      results.failed++;
      results.errors.push({ snapshotId, error: error.message });
    }
  }

  return results;
};
